import { useEffect, useState } from 'react';
import {
  Close as CloseIcon,
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useMutation } from '@tanstack/react-query';
import { FileItem, FileUploader, downloadFileFromUrl } from '@/common';
import { Report } from '../types';

import { addFileToReport } from '../api/addFileToReport';
import { downloadFileFromReport } from '../api/downloadFileFromReport';
import { downloadFileFromResponse, getMessageFromError } from '@/core';
import { downloadFileFromDws } from '../api/downloadFileFromDs';
import { checkIfErrorIsAccessAuthorizationError } from '@/core/utils/getMessageFromError';

type ReportFilesModalScreen = 'file-list' | 'add-files';

type ReportFilesModalProps = {
  show: boolean;
  setShow: (show: boolean) => void;
  setReports: (reports: Report[]) => void;
  reportId: string;
  reports?: Report[];
  disabled?: boolean;
};

const ReportFilesModal = ({
  show,
  disabled = false,
  setShow,
  setReports,
  reportId,
  reports,
}: ReportFilesModalProps) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const [screen, setScreen] = useState<ReportFilesModalScreen>('file-list');

  const downloadFileMutation = useMutation({
    mutationFn: downloadFileFromReport,
    onSuccess: async (response) => {
      await downloadFileFromResponse(response);
      enqueueSnackbar(
        t('Successfully downloaded file.', { defaultValue: 'Successfully downloaded file.' }),
        { variant: 'success' }
      );
    },
    onError: (error: Error) => {
      checkIfErrorIsAccessAuthorizationError(error)
        ? enqueueSnackbar(
            t('No access authorisation!', { defaultValue: 'No access authorisation!' }),
            { variant: 'error', anchorOrigin: { vertical: 'top', horizontal: 'center' } }
          )
        : enqueueSnackbar(getMessageFromError(error), { variant: 'error' });
    },
  });
  const downloadFileFromDsMutation = useMutation({
    mutationFn: downloadFileFromDws,
    onSuccess: async (result) => {
      const res = result.res;
      const blob = await res.blob();
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', result.filename);

      // Append to html link element page
      document.body.appendChild(link);

      // Start download
      link.click();

      enqueueSnackbar(
        t('Successfully downloaded file.', { defaultValue: 'Successfully downloaded file.' }),
        { variant: 'success' }
      );
    },
    onError: (error: Error) => {
      checkIfErrorIsAccessAuthorizationError(error)
        ? enqueueSnackbar(
            t('No access authorisation!', { defaultValue: 'No access authorisation!' }),
            { variant: 'error', anchorOrigin: { vertical: 'top', horizontal: 'center' } }
          )
        : enqueueSnackbar(getMessageFromError(error), { variant: 'error' });
    },
  });

  useEffect(() => {
    // Hacky way to solve flickering - file-list is shown before modal closing animation is fully finished
    const timeout = setTimeout(() => {
      if (!show) {
        setScreen('file-list');
      }
    }, 100);

    return () => clearTimeout(timeout);
  }, [show]);

  const report =
    reports && reports.length > 0 ? reports.find((rep) => rep.id === reportId) : undefined;

  return (
    <Dialog fullScreen={fullScreen} open={show} onClose={() => setShow(false)} fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h5">
          {screen === 'file-list' ? (
            t('Report - Files', { defaultValue: 'Report - Files' })
          ) : (
            <Box>
              <IconButton onClick={() => setScreen('file-list')} sx={{ p: 0, mr: '0.5rem' }}>
                <ArrowBackIcon />
              </IconButton>
              {t('Report - Upload files', { defaultValue: 'Report - Upload files' })}
            </Box>
          )}
        </Typography>
        <CloseIcon sx={{ fontSize: '2rem', cursor: 'pointer' }} onClick={() => setShow(false)} />
      </DialogTitle>
      <DialogContent>
        {screen === 'file-list' ? (
          <>
            <Button
              onClick={() => setScreen('add-files')}
              variant="outlined"
              startIcon={<AddIcon />}
              fullWidth
              disabled={
                disabled || (report?.status ? Number(report?.status.slice(0, 2)) >= 10 : false)
              }
            >
              {t('Add files', { defaultValue: 'Add files' })}
            </Button>
            <Box>
              {report && report.files.length > 0 ? (
                <List>
                  {report.files.map((item) => (
                    <FileItem
                      key={item.id}
                      name={item.title || ''}
                      uploadedBy={item.uploadedBy}
                      uploadedAt={item.uploadedAt}
                      fileName={item.fileName}
                      onDownload={() =>
                        item.dwsDok
                          ? downloadFileFromDsMutation.mutate({
                              dok: item.dwsDok,
                              filename: item.title || item.dwsDok.originalfilename,
                            })
                          : downloadFileMutation.mutate({
                              id: reportId,
                              fileId: item.id,
                            })
                      }
                    />
                  ))}
                </List>
              ) : (
                <Typography variant="subtitle1">
                  {t('No files', { defaultValue: 'No files' })}
                </Typography>
              )}
            </Box>
          </>
        ) : (
          <FileUploader
            // eslint-disable-next-line consistent-return
            onFileUpload={async (file) => {
              const formData = new FormData();
              if (report) {
                const mergedFiles = [...report.files];
                if (!mergedFiles.find((mf) => mf.title === file.file.name)) {
                  formData.append('title', file.file.name);
                  formData.append('attachments', file.file);

                  // eslint-disable-next-line no-promise-executor-return
                  await new Promise((r) => setTimeout(r, 800));
                }

                return addFileToReport({ id: reportId, formData });
              }
            }}
            onUploadFinished={(allFilesSuccessfullyUploaded, newFiles) => {
              // invalidateAuditorVisit();
              if (report) {
                const mergedFiles = [...report.files];
                newFiles.forEach((f) => {
                  if (!mergedFiles.find((mf) => mf.title === f.file.name)) {
                    mergedFiles.push({
                      title: f.file.name,
                      id: '',
                      uploadedBy: '',
                      uploadedAt: '',
                    });
                  }
                });

                report.files = mergedFiles;
              }

              if (reports) {
                setReports(reports);
              }

              // napiši update v stautu za file

              if (allFilesSuccessfullyUploaded) {
                enqueueSnackbar(
                  t('Successfully uploaded file.', {
                    defaultValue: 'Successfully uploaded file.',
                  }),
                  { variant: 'success' }
                );
                setScreen('file-list');
              }
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReportFilesModal;
